import { useState, useCallback, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

interface ListenerStatus {
  listenerId: string;
  status: 'registered' | 'received' | 'verified' | 'failed';
  webhookEndpoint: string;
  documentHash: string;
  envelopeId?: string;
  signedDocumentHash?: string;
  originalHashFromSignature?: string;
  receivedAt?: string;
  verifiedAt?: string;
}

export default function TestPage() {
  const [file, setFile] = useState<File | null>(null);
  const [documentHash, setDocumentHash] = useState<string>('');
  const [isHashing, setIsHashing] = useState(false);
  const [registerWithDocusign, setRegisterWithDocusign] = useState(true);
  const [signerEmail, setSignerEmail] = useState('');
  const [signerName, setSignerName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listener, setListener] = useState<ListenerStatus | null>(null);
  const [pollingInterval, setPollingInterval] = useState<ReturnType<typeof setInterval> | null>(null);

  // Poll for listener status updates
  useEffect(() => {
    if (listener && listener.status === 'registered') {
      const interval = setInterval(async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/listener?listenerId=${listener.listenerId}`);
          if (response.ok) {
            const data = await response.json();
            setListener({
              listenerId: data.listener_id,
              status: data.status,
              webhookEndpoint: data.webhook_endpoint,
              documentHash: data.document_hash,
              envelopeId: data.envelope_id,
              signedDocumentHash: data.signed_document_hash,
              originalHashFromSignature: data.original_hash_from_signature,
              receivedAt: data.received_at,
              verifiedAt: data.verified_at,
            });

            // Stop polling if status is no longer 'registered'
            if (data.status !== 'registered') {
              clearInterval(interval);
              setPollingInterval(null);
            }
          }
        } catch (err) {
          console.error('Error polling listener status:', err);
        }
      }, 3000); // Poll every 3 seconds

      setPollingInterval(interval);

      return () => {
        clearInterval(interval);
        setPollingInterval(null);
      };
    }
  }, [listener?.listenerId, listener?.status]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const calculateSHA256 = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    return `0x${hashHex}`;
  };

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setIsHashing(true);
    setError(null);
    setListener(null);

    try {
      const hash = await calculateSHA256(selectedFile);
      setDocumentHash(hash);
    } catch (err) {
      console.error('Error calculating file hash:', err);
      setError('Failed to calculate file hash');
    } finally {
      setIsHashing(false);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!file || !documentHash) {
      setError('Please select a file first');
      return;
    }

    if (!signerEmail || !signerName) {
      setError('Please enter signer email and name');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Step 1: Register the document hash listener
      const registerResponse = await fetch(`${API_BASE_URL}/api/listener/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: signerEmail,
          documentHash: documentHash,
        }),
      });

      if (!registerResponse.ok) {
        const errData = await registerResponse.json();
        throw new Error(errData.error || 'Failed to register listener');
      }

      const listenerData = await registerResponse.json();
      setListener({
        listenerId: listenerData.listenerId,
        status: listenerData.status,
        webhookEndpoint: listenerData.webhookEndpoint,
        documentHash: documentHash,
      });

      // Step 2: If registering with DocuSign, send the document for signing
      if (registerWithDocusign) {
        // Use localStorage userId or fallback to 'default' for pre-configured credentials
        const userId = localStorage.getItem('docusign_user_id') || 'default';

        // Read file content as base64
        const fileContent = await readFileAsBase64(file);

        const sendResponse = await fetch(`${API_BASE_URL}/api/envelope/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            signerEmail,
            signerName,
            subject: `Sign: ${file.name}`,
            documentBase64: fileContent,
            documentName: file.name,
            originalHash: documentHash,
          }),
        });

        if (!sendResponse.ok) {
          const errData = await sendResponse.json();
          throw new Error(errData.error || 'Failed to send envelope');
        }

        const envelopeData = await sendResponse.json();
        setListener((prev) =>
          prev
            ? {
                ...prev,
                envelopeId: envelopeData.envelopeId,
              }
            : null
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix if present
        const base64 = result.split(',')[1] || result;
        resolve(base64);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'registered':
        return 'bg-yellow-100 text-yellow-800';
      case 'received':
        return 'bg-blue-100 text-blue-800';
      case 'verified':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const resetForm = () => {
    setFile(null);
    setDocumentHash('');
    setListener(null);
    setError(null);
    setSignerEmail('');
    setSignerName('');
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Document Registration Test</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* File Upload Section */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">Select PDF Document</label>
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-indigo-400 transition-colors">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={isHashing || isSubmitting}
                  />
                  <div className="text-center">
                    {file ? (
                      <div className="flex items-center justify-center space-x-2">
                        <svg
                          className="w-8 h-8 text-green-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span className="text-sm text-gray-600">{file.name}</span>
                      </div>
                    ) : (
                      <>
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <p className="mt-1 text-sm text-gray-600">
                          {isHashing ? 'Calculating hash...' : 'Click or drag to upload PDF'}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Document Hash Display */}
          {documentHash && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Document Hash (SHA-256)</label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={documentHash}
                  readOnly
                  className="flex-1 px-3 py-2 border rounded-md bg-gray-50 font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(documentHash)}
                  className="px-3 py-2 text-sm text-indigo-600 hover:text-indigo-800"
                >
                  Copy
                </button>
              </div>
            </div>
          )}

          {/* Signer Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Signer Email</label>
              <input
                type="email"
                value={signerEmail}
                onChange={(e) => setSignerEmail(e.target.value)}
                placeholder="signer@example.com"
                className="w-full px-3 py-2 border rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Signer Name</label>
              <input
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="John Doe"
                className="w-full px-3 py-2 border rounded-md"
                required
              />
            </div>
          </div>

          {/* DocuSign Checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="registerDocusign"
              checked={registerWithDocusign}
              onChange={(e) => setRegisterWithDocusign(e.target.checked)}
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <label htmlFor="registerDocusign" className="ml-2 text-sm text-gray-700">
              Send to DocuSign for signing
            </label>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">{error}</div>
          )}

          {/* Submit Button */}
          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={!file || !documentHash || isSubmitting || isHashing}
              className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Processing...' : 'Register & Send'}
            </button>
            {listener && (
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Reset
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Listener Status Panel */}
      {listener && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Registration Status</h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Status</span>
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(listener.status)}`}>
                {listener.status.charAt(0).toUpperCase() + listener.status.slice(1)}
                {listener.status === 'registered' && (
                  <span className="ml-2 animate-pulse">Waiting...</span>
                )}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Listener ID</span>
              <span className="font-mono text-sm">{listener.listenerId}</span>
            </div>

            {listener.envelopeId && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Envelope ID</span>
                <span className="font-mono text-sm">{listener.envelopeId}</span>
              </div>
            )}

            <div className="pt-2 border-t">
              <p className="text-sm text-gray-500 mb-2">Original Hash (Registered)</p>
              <p className="font-mono text-xs bg-gray-50 p-2 rounded break-all">{listener.documentHash}</p>
            </div>

            {listener.originalHashFromSignature && (
              <div className="pt-2 border-t">
                <p className="text-sm text-gray-500 mb-2">Hash from Signed PDF</p>
                <p className="font-mono text-xs bg-gray-50 p-2 rounded break-all">
                  {listener.originalHashFromSignature}
                </p>
                {listener.status === 'verified' && (
                  <p className="mt-2 text-green-600 text-sm flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Hash verified! Document integrity confirmed.
                  </p>
                )}
              </div>
            )}

            {listener.signedDocumentHash && (
              <div className="pt-2 border-t">
                <p className="text-sm text-gray-500 mb-2">Signed PDF Hash (includes signature)</p>
                <p className="font-mono text-xs bg-gray-50 p-2 rounded break-all">
                  {listener.signedDocumentHash}
                </p>
              </div>
            )}

            {listener.receivedAt && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Webhook Received</span>
                <span>{new Date(listener.receivedAt).toLocaleString()}</span>
              </div>
            )}

            {listener.verifiedAt && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Verified At</span>
                <span>{new Date(listener.verifiedAt).toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-2">How it works:</h3>
        <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
          <li>Select a PDF document to calculate its SHA-256 hash</li>
          <li>Enter the signer's email and name</li>
          <li>Click "Register & Send" to register the hash and send for signing</li>
          <li>The document will be sent to DocuSign for the signer to sign</li>
          <li>When signed, the webhook will be received and the hash will be verified</li>
          <li>If the original hash matches, the status will change to "Verified"</li>
        </ol>
      </div>
    </div>
  );
}
